import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';

import { Button, Card, Field, Input, OptionGroup, OptionRow, Skeleton } from '@/design-system';
import {
    slugFor,
    TENANT_SLUG_PATTERN,
    type PlatformTemplate,
    type ProvisionedTenant,
} from '@/entities/tenant';
import { errorMessage, isApiError } from '@/shared/api';

import { paths } from '@/app/router/paths';
import { AdminHeader } from './components/AdminHeader';
import { adminCopy, formatCredits } from './copy';
import { useCreateTenant, usePlatformTemplates } from './hooks';

interface Draft {
    name: string;
    slug: string;
    email: string;
    phone: string;
    ownerName: string;
    ownerEmail: string;
    ownerPhone: string;
    credits: string;
    templates: string[] | null;
}

const EMPTY: Draft = {
    name: '',
    slug: '',
    email: '',
    phone: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    credits: '0',
    templates: null,
};

/** De velden van `ProvisionTenantRequest`, zoals een 422 ze noemt. */
type ApiField =
    | 'name'
    | 'slug'
    | 'email'
    | 'phone'
    | 'owner.name'
    | 'owner.email'
    | 'owner.phone'
    | 'starting_credits'
    | 'templates';

function orNull(value: string): string | null {
    return value.trim() === '' ? null : value.trim();
}

/**
 * Platformbeheer - een keuringsbedrijf aanmaken (`POST /platform/tenants`).
 *
 * Eén formulier voor alles wat een werkend bedrijf nodig heeft: de naam, de
 * eerste eigenaar, een startsaldo en de vragenlijsten. De API zet het in één
 * transactie neer; lukt één deel niet, dan blijft er niets half staan en staat
 * de melding bij het veld.
 */
export function AdminNewTenantPage() {
    const create = useCreateTenant();
    const templates = usePlatformTemplates();
    const [draft, setDraft] = useState<Draft>(EMPTY);
    const [slugTouched, setSlugTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    /* Tot iemand ze zelf kiest: alle vragenlijsten van het platform. */
    const chosen = draft.templates ?? templates.data?.map((template) => template.uuid) ?? [];
    const credits = /^\d+$/.test(draft.credits.trim()) ? Number(draft.credits.trim()) : null;

    const local: Partial<Record<ApiField, string>> = submitted
        ? {
              ...(draft.name.trim() === '' ? { name: adminCopy.createRequired } : {}),
              ...(draft.slug === ''
                  ? { slug: adminCopy.createRequired }
                  : TENANT_SLUG_PATTERN.test(draft.slug)
                    ? {}
                    : { slug: adminCopy.createSlugInvalid }),
              ...(draft.ownerName.trim() === '' ? { 'owner.name': adminCopy.createRequired } : {}),
              ...(draft.ownerEmail.trim() === ''
                  ? { 'owner.email': adminCopy.createRequired }
                  : {}),
              ...(credits === null ? { starting_credits: adminCopy.createCreditsInvalid } : {}),
          }
        : {};

    const fieldError = (field: ApiField): string | undefined =>
        local[field] ??
        (isApiError(create.error) ? create.error.firstValidationError(field) : undefined);
    const hasFieldErrors =
        isApiError(create.error) && Object.keys(create.error.validationErrors).length > 0;

    function patch(next: Partial<Draft>) {
        setDraft((current) => ({ ...current, ...next }));
    }

    function submit(event: FormEvent) {
        event.preventDefault();
        setSubmitted(true);

        const valid =
            draft.name.trim() !== '' &&
            TENANT_SLUG_PATTERN.test(draft.slug) &&
            draft.ownerName.trim() !== '' &&
            draft.ownerEmail.trim() !== '' &&
            credits !== null;

        if (!valid || credits === null) {
            return;
        }

        create.mutate({
            name: draft.name.trim(),
            slug: draft.slug,
            email: orNull(draft.email),
            phone: orNull(draft.phone),
            owner: {
                name: draft.ownerName.trim(),
                email: draft.ownerEmail.trim(),
                phone: orNull(draft.ownerPhone),
            },
            startingCredits: credits,
            templateUuids: chosen,
        });
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <AdminHeader
                back={{ to: paths.adminTenants, label: adminCopy.createBack }}
                eyebrow={`${adminCopy.eyebrowPlatform} · ${adminCopy.createEyebrow}`}
                title={adminCopy.createTitle}
            />

            <div className="flex-1 overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8">
                {create.isSuccess ? (
                    <Created
                        provisioned={create.data}
                        onAnother={() => {
                            create.reset();
                            setDraft(EMPTY);
                            setSlugTouched(false);
                            setSubmitted(false);
                        }}
                    />
                ) : (
                    <form
                        onSubmit={submit}
                        noValidate
                        className="grid max-w-[880px] grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-start gap-[14px]"
                    >
                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-secondary)] [grid-column:1/-1]">
                            {adminCopy.createIntro}
                        </p>

                        <Card padding="md" as="fieldset" className="m-0 grid gap-[10px]">
                            <legend className="sr-only">{adminCopy.createCompany}</legend>
                            <h2 className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                                {adminCopy.createCompany}
                            </h2>
                            <Field
                                label={adminCopy.createName}
                                size="sm"
                                error={fieldError('name')}
                            >
                                <Input
                                    value={draft.name}
                                    autoComplete="organization"
                                    onChange={(event) =>
                                        patch({
                                            name: event.target.value,
                                            ...(slugTouched
                                                ? {}
                                                : { slug: slugFor(event.target.value) }),
                                        })
                                    }
                                />
                            </Field>
                            <Field
                                label={adminCopy.createSlug}
                                size="sm"
                                description={adminCopy.createSlugHint}
                                error={fieldError('slug')}
                            >
                                <Input
                                    mono
                                    value={draft.slug}
                                    autoComplete="off"
                                    spellCheck={false}
                                    onChange={(event) => {
                                        setSlugTouched(true);
                                        patch({ slug: event.target.value.toLowerCase() });
                                    }}
                                />
                            </Field>
                            <Field
                                label={adminCopy.createEmail}
                                size="sm"
                                error={fieldError('email')}
                            >
                                <Input
                                    type="email"
                                    value={draft.email}
                                    autoComplete="off"
                                    onChange={(event) => patch({ email: event.target.value })}
                                />
                            </Field>
                            <Field
                                label={adminCopy.createPhone}
                                size="sm"
                                error={fieldError('phone')}
                            >
                                <Input
                                    type="tel"
                                    value={draft.phone}
                                    autoComplete="off"
                                    onChange={(event) => patch({ phone: event.target.value })}
                                />
                            </Field>
                        </Card>

                        <div className="grid gap-[14px]">
                            <Card padding="md" as="fieldset" className="m-0 grid gap-[10px]">
                                <legend className="sr-only">{adminCopy.createOwner}</legend>
                                <h2 className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                                    {adminCopy.createOwner}
                                </h2>
                                <Field
                                    label={adminCopy.createOwnerName}
                                    size="sm"
                                    error={fieldError('owner.name')}
                                >
                                    <Input
                                        value={draft.ownerName}
                                        autoComplete="off"
                                        onChange={(event) =>
                                            patch({ ownerName: event.target.value })
                                        }
                                    />
                                </Field>
                                <Field
                                    label={adminCopy.createOwnerEmail}
                                    size="sm"
                                    error={fieldError('owner.email')}
                                >
                                    <Input
                                        type="email"
                                        value={draft.ownerEmail}
                                        autoComplete="off"
                                        spellCheck={false}
                                        onChange={(event) =>
                                            patch({ ownerEmail: event.target.value })
                                        }
                                    />
                                </Field>
                                <Field
                                    label={adminCopy.createOwnerPhone}
                                    size="sm"
                                    error={fieldError('owner.phone')}
                                >
                                    <Input
                                        type="tel"
                                        value={draft.ownerPhone}
                                        autoComplete="off"
                                        onChange={(event) =>
                                            patch({ ownerPhone: event.target.value })
                                        }
                                    />
                                </Field>
                            </Card>

                            <Card padding="md" as="fieldset" className="m-0 grid gap-[10px]">
                                <legend className="sr-only">{adminCopy.createCredits}</legend>
                                <Field
                                    label={adminCopy.createCredits}
                                    size="sm"
                                    description={adminCopy.createCreditsHint}
                                    error={fieldError('starting_credits')}
                                    className="max-w-[220px]"
                                >
                                    <Input
                                        mono
                                        inputMode="numeric"
                                        value={draft.credits}
                                        onChange={(event) => patch({ credits: event.target.value })}
                                    />
                                </Field>

                                <TemplatePicker
                                    templates={templates.data ?? null}
                                    chosen={chosen}
                                    error={fieldError('templates')}
                                    onChange={(next) => patch({ templates: next })}
                                />
                            </Card>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 [grid-column:1/-1]">
                            <Button
                                type="submit"
                                variant="primary"
                                size="md"
                                disabled={create.isPending}
                            >
                                {create.isPending ? adminCopy.createBusy : adminCopy.createSubmit}
                            </Button>
                            {create.error && !hasFieldErrors ? (
                                <p
                                    role="alert"
                                    className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]"
                                >
                                    {errorMessage(create.error)}
                                </p>
                            ) : null}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

function TemplatePicker({
    templates,
    chosen,
    error,
    onChange,
}: {
    templates: PlatformTemplate[] | null;
    chosen: string[];
    error?: string;
    onChange: (next: string[]) => void;
}) {
    return (
        <div>
            <p
                id="new-tenant-templates"
                className="m-0 text-[12px] leading-[1.3] text-[var(--text-secondary)]"
            >
                {adminCopy.createTemplates}
            </p>
            <p className="m-0 mt-[2px] text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                {adminCopy.createTemplatesHint}
            </p>

            {templates === null ? (
                <Skeleton height={44} className="mt-2" label={adminCopy.createTemplatesLoading} />
            ) : (
                <OptionGroup type="check" labelledBy="new-tenant-templates" className="mt-1 px-0">
                    {templates.map((template, index) => (
                        <OptionRow
                            key={template.uuid}
                            index={index}
                            checked={chosen.includes(template.uuid)}
                            onSelect={(on) =>
                                onChange(
                                    on
                                        ? [...chosen, template.uuid]
                                        : chosen.filter((uuid) => uuid !== template.uuid),
                                )
                            }
                        >
                            {`${template.name} · ${template.norm}`}
                        </OptionRow>
                    ))}
                </OptionGroup>
            )}

            {error ? (
                <p className="m-0 mt-1 text-[12px] leading-[1.4] text-[var(--danger)]">{error}</p>
            ) : null}
        </div>
    );
}

function Created({
    provisioned,
    onAnother,
}: {
    provisioned: ProvisionedTenant;
    onAnother: () => void;
}) {
    const navigate = useNavigate();
    const { tenant, owner } = provisioned;

    return (
        <Card padding="lg" as="section" aria-labelledby="created-title" className="max-w-[640px]">
            <h2
                id="created-title"
                className="m-0 font-[family-name:var(--font-display)] text-[17px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {adminCopy.createdTitle(tenant.name)}
            </h2>
            <ul className="m-0 mt-3 grid list-none gap-[6px] p-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                <li>{adminCopy.createdTrial}</li>
                <li>
                    {owner.existingAccount
                        ? adminCopy.createdExisting(owner.name, owner.email)
                        : adminCopy.createdInvited(owner.email)}
                </li>
                <li>{adminCopy.createdCredits(formatCredits(provisioned.startingCredits))}</li>
                <li>
                    {provisioned.templates.length > 0
                        ? adminCopy.createdTemplates(
                              provisioned.templates.map((template) => template.name).join(', '),
                          )
                        : adminCopy.createdNoTemplates}
                </li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => navigate(paths.adminTenant(tenant.uuid))}
                >
                    {adminCopy.createdOpen}
                </Button>
                <Button variant="outline" size="md" onClick={onAnother}>
                    {adminCopy.createdAnother}
                </Button>
            </div>
        </Card>
    );
}
